export const SIGNUP_CAPTCHA_ERROR_REQUIRED = 'signup-captcha-required';
export const SIGNUP_CAPTCHA_ERROR_FAILED = 'signup-captcha-failed';
export const SIGNUP_CAPTCHA_ACTION = 'signup';

const RECAPTCHA_V3_SCRIPT_ID = 'google-recaptcha-v3-script';
const RECAPTCHA_V3_SCRIPT_HOSTS = ['https://www.google.com', 'https://www.recaptcha.net'];

export const getSignupCaptchaSiteKey = () => {
  return (
    process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY ||
    process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
    process.env.REACT_APP_CAPTCHA_SITE_KEY ||
    ''
  );
};

export const isSignupCaptchaEnabled = () => {
  return !!getSignupCaptchaSiteKey();
};

const createRecaptchaScriptSrc = (siteKey, host) => {
  return `${host}/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
};

let recaptchaScriptPromise = null;

const getCaptchaDiagnosticMessage = error => error?.message || error?.type || null;
const isInvalidSiteKeyLoadError = error =>
  /invalid site key|not loaded in api\.js/i.test(getCaptchaDiagnosticMessage(error) || '');

const isRecaptchaScriptForSiteKey = (script, siteKey) => {
  if (!script?.src || !siteKey) {
    return false;
  }

  try {
    const src = new URL(script.src, window.location.href);
    return src.pathname.endsWith('/recaptcha/api.js') && src.searchParams.get('render') === siteKey;
  } catch (_error) {
    return script.src.includes(`render=${encodeURIComponent(siteKey)}`);
  }
};

const isRecaptchaApiScript = script => {
  if (!script?.src) {
    return false;
  }

  try {
    const src = new URL(script.src, window.location.href);
    return /\/recaptcha\/(?:api|enterprise)\.js$/.test(src.pathname);
  } catch (_error) {
    return /\/recaptcha\/(?:api|enterprise)\.js/.test(script.src);
  }
};

const getRecaptchaScriptForSiteKey = siteKey => {
  if (typeof document === 'undefined') {
    return null;
  }

  return Array.from(document.querySelectorAll('script[src]')).find(script =>
    isRecaptchaScriptForSiteKey(script, siteKey)
  );
};

const getExecutableRecaptchaClient = grecaptcha => {
  if (grecaptcha && typeof grecaptcha.execute === 'function') {
    return grecaptcha;
  }

  if (grecaptcha?.enterprise && typeof grecaptcha.enterprise.execute === 'function') {
    return grecaptcha.enterprise;
  }

  return null;
};

const getReadyRecaptchaClient = grecaptcha => {
  if (grecaptcha?.enterprise && typeof grecaptcha.enterprise.ready === 'function') {
    return grecaptcha.enterprise;
  }

  if (grecaptcha && typeof grecaptcha.ready === 'function') {
    return grecaptcha;
  }

  return null;
};

const signupCaptchaWarning = (reason, error, extra = {}) => {
  if (typeof window !== 'undefined' && window.console && typeof window.console.warn === 'function') {
    window.console.warn('Signup reCAPTCHA failed', {
      reason,
      message: getCaptchaDiagnosticMessage(error),
      ...extra,
    });
  }
};

const createExecuteCaptchaError = ({ reason, error, action }) =>
  createSignupCaptchaApiError(SIGNUP_CAPTCHA_ERROR_FAILED, {
    reason,
    message: getCaptchaDiagnosticMessage(error),
    action,
  });

const resetRecaptchaGlobals = () => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    delete window.grecaptcha;
  } catch (_error) {
    window.grecaptcha = undefined;
  }

  try {
    delete window.___grecaptcha_cfg;
  } catch (_error) {
    window.___grecaptcha_cfg = undefined;
  }
};

const removeExistingRecaptchaScript = ({ includeAll = false } = {}) => {
  const scripts = includeAll
    ? Array.from(document.querySelectorAll('script[src]')).filter(isRecaptchaApiScript)
    : [document.getElementById(RECAPTCHA_V3_SCRIPT_ID)].filter(Boolean);

  scripts.forEach(script => {
    if (script?.parentNode) {
      script.parentNode.removeChild(script);
    }
  });
};

const resetRecaptchaApiState = () => {
  recaptchaScriptPromise = null;
  removeExistingRecaptchaScript({ includeAll: true });
  resetRecaptchaGlobals();
};

const loadRecaptchaScriptFromHost = (siteKey, host, { resetGlobal = false } = {}) => {
  return new Promise((resolve, reject) => {
    if (resetGlobal) {
      resetRecaptchaApiState();
    } else {
      removeExistingRecaptchaScript();
    }

    const script = document.createElement('script');
    script.id = RECAPTCHA_V3_SCRIPT_ID;
    script.src = createRecaptchaScriptSrc(siteKey, host);
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.grecaptcha || null);
    script.onerror = error => {
      removeExistingRecaptchaScript();
      reject(error || new Error('reCAPTCHA script failed to load.'));
    };
    document.body.appendChild(script);
  });
};

const loadRecaptchaScriptWithFallback = (siteKey, hostIndex = 0, options = {}) => {
  const host = RECAPTCHA_V3_SCRIPT_HOSTS[hostIndex];

  return loadRecaptchaScriptFromHost(siteKey, host, options).catch(error => {
    signupCaptchaWarning('script-load-failed', error, { host });

    if (hostIndex + 1 < RECAPTCHA_V3_SCRIPT_HOSTS.length) {
      return loadRecaptchaScriptWithFallback(siteKey, hostIndex + 1, options);
    }

    throw error;
  });
};

export const loadRecaptchaV3 = (
  siteKey = getSignupCaptchaSiteKey(),
  { forceReload = false } = {}
) => {
  if (!siteKey) {
    return Promise.resolve(null);
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(
      createSignupCaptchaApiError(SIGNUP_CAPTCHA_ERROR_FAILED, {
        reason: 'browser-unavailable',
      })
    );
  }

  const siteKeyScript = getRecaptchaScriptForSiteKey(siteKey);
  const shouldResetExistingRecaptcha =
    forceReload || Boolean(window.grecaptcha && !siteKeyScript);

  if (!forceReload && window.grecaptcha && siteKeyScript) {
    return Promise.resolve(window.grecaptcha);
  }

  if (!forceReload && recaptchaScriptPromise && siteKeyScript) {
    return recaptchaScriptPromise;
  }
  recaptchaScriptPromise = null;

  recaptchaScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(RECAPTCHA_V3_SCRIPT_ID);
    if (existingScript) {
      if (!isRecaptchaScriptForSiteKey(existingScript, siteKey)) {
        loadRecaptchaScriptWithFallback(siteKey, 0, {
          resetGlobal: shouldResetExistingRecaptcha,
        })
          .then(resolve)
          .catch(reject);
        return;
      }

      existingScript.addEventListener('load', () => resolve(window.grecaptcha || null), {
        once: true,
      });
      existingScript.addEventListener(
        'error',
        error => {
          recaptchaScriptPromise = null;
          reject(
            createSignupCaptchaApiError(SIGNUP_CAPTCHA_ERROR_FAILED, {
              reason: 'script-load-failed',
              message: getCaptchaDiagnosticMessage(error),
            })
          );
        },
        { once: true }
      );
      return;
    }

    loadRecaptchaScriptWithFallback(siteKey, 0, {
      resetGlobal: shouldResetExistingRecaptcha,
    })
      .then(resolve)
      .catch(reject);
  })
    .then(grecaptcha => {
      if (!grecaptcha) {
        throw new Error('reCAPTCHA API is unavailable.');
      }

      return grecaptcha;
    })
    .catch(error => {
      recaptchaScriptPromise = null;
      signupCaptchaWarning('script-unavailable', error);
      return Promise.reject(
        createSignupCaptchaApiError(SIGNUP_CAPTCHA_ERROR_FAILED, {
          reason: 'script-unavailable',
          message: getCaptchaDiagnosticMessage(error),
        })
      );
    });

  return recaptchaScriptPromise;
};

const executeCaptchaWithClient = ({ action, siteKey, grecaptcha, retryOnInvalidLoad = true }) => {
  return new Promise((resolve, reject) => {
    const runCaptcha = () => {
      const recaptchaClient = getExecutableRecaptchaClient(grecaptcha);

      if (!recaptchaClient) {
        signupCaptchaWarning('execute-unavailable', null, { action });
        reject(
          createSignupCaptchaApiError(SIGNUP_CAPTCHA_ERROR_FAILED, {
            reason: 'execute-unavailable',
            action,
          })
        );
        return;
      }

      try {
        recaptchaClient
          .execute(siteKey, { action })
          .then(token => {
            if (!token) {
              signupCaptchaWarning('empty-token');
              reject(
                createSignupCaptchaApiError(SIGNUP_CAPTCHA_ERROR_FAILED, {
                  reason: 'empty-token',
                  action,
                })
              );
              return;
            }
            resolve(token);
          })
          .catch(error => {
            if (retryOnInvalidLoad && isInvalidSiteKeyLoadError(error)) {
              signupCaptchaWarning('execute-retrying-after-invalid-load', error, { action });
              resetRecaptchaApiState();
              loadRecaptchaV3(siteKey, { forceReload: true })
                .then(reloadedRecaptcha =>
                  executeCaptchaWithClient({
                    action,
                    siteKey,
                    grecaptcha: reloadedRecaptcha,
                    retryOnInvalidLoad: false,
                  })
                )
                .then(resolve)
                .catch(reject);
              return;
            }

            if (isInvalidSiteKeyLoadError(error)) {
              signupCaptchaWarning('invalid-site-key-or-script-load', error, { action });
              reject(
                createExecuteCaptchaError({
                  reason: 'invalid-site-key-or-script-load',
                  error,
                  action,
                })
              );
              return;
            }

            signupCaptchaWarning('execute-rejected', error, { action });
            reject(
              createExecuteCaptchaError({
                reason: 'execute-rejected',
                error,
                action,
              })
            );
          });
      } catch (error) {
        if (retryOnInvalidLoad && isInvalidSiteKeyLoadError(error)) {
          signupCaptchaWarning('execute-retrying-after-invalid-load', error, { action });
          resetRecaptchaApiState();
          loadRecaptchaV3(siteKey, { forceReload: true })
            .then(reloadedRecaptcha =>
              executeCaptchaWithClient({
                action,
                siteKey,
                grecaptcha: reloadedRecaptcha,
                retryOnInvalidLoad: false,
              })
            )
            .then(resolve)
            .catch(reject);
          return;
        }

        if (isInvalidSiteKeyLoadError(error)) {
          signupCaptchaWarning('invalid-site-key-or-script-load', error, { action });
          reject(
            createExecuteCaptchaError({
              reason: 'invalid-site-key-or-script-load',
              error,
              action,
            })
          );
          return;
        }

        signupCaptchaWarning('execute-threw', error, { action });
        reject(
          createExecuteCaptchaError({
            reason: 'execute-threw',
            error,
            action,
          })
        );
      }
    };

    try {
      const readyClient = getReadyRecaptchaClient(grecaptcha);

      if (readyClient) {
        readyClient.ready(runCaptcha);
      } else {
        runCaptcha();
      }
    } catch (error) {
      signupCaptchaWarning('ready-threw', error, { action });
      reject(
        createSignupCaptchaApiError(SIGNUP_CAPTCHA_ERROR_FAILED, {
          reason: 'ready-threw',
          message: getCaptchaDiagnosticMessage(error),
          action,
        })
      );
    }
  });
};

export const executeSignupCaptcha = ({ action = SIGNUP_CAPTCHA_ACTION } = {}) => {
  const siteKey = getSignupCaptchaSiteKey();
  if (!siteKey) {
    return Promise.resolve(null);
  }

  return loadRecaptchaV3(siteKey).then(grecaptcha =>
    executeCaptchaWithClient({ action, siteKey, grecaptcha })
  );
};

const createApiError = (code, meta = {}) => {
  const metaMaybe = Object.keys(meta).length > 0 ? { meta } : {};

  return {
    status: 400,
    code,
    title: 'Signup could not be verified',
    detail: 'We could not verify your signup request. Please try again.',
    source: 'signup',
    ...metaMaybe,
  };
};

export const createSignupCaptchaApiError = (code, meta = {}) => {
  const apiError = createApiError(code, meta);
  return {
    status: 400,
    statusText: 'We could not verify your signup request. Please try again.',
    data: {
      errors: [apiError],
    },
    apiErrors: [apiError],
  };
};
